import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import React from "react";
import { format } from "date-fns";
import { styled } from "@mui/material/styles";
import {
  type GridColDef,
  type GridRenderCellParams,
  type GridSelectionModel,
  type GridValueGetterParams,
} from "@mui/x-data-grid-pro";
import { EmailManagementListDataGrid } from "../../../components/DataGrid";
import {
  type DataGridRow,
  type StoredPicturesDownloadCondition,
  type StoredPicturesFeedGridProps,
} from "../types";
import {
  PictureTypeLabels,
  PictureTypeValues,
  RealEstateTypeLabels,
  RealEstateTypeValues,
} from "../types/enums";
import { Box, Button, IconButton, Tooltip } from "@mui/material";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import { ButtonVariantOption, LinkButton } from "@/components/Button";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { downloadStoredPicturesAPI } from "@/api/downloadStoredPicturesAPI";
import { type AxiosInstance } from "axios";
import { type NavigateFunction } from "react-router-dom";
import { CustomColumnMenu } from "./CustomColumnMenu";

const StyledDataGrid = styled(EmailManagementListDataGrid)`
  // テーブルのセルをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-cell:focus-within,
  & .MuiDataGrid-cell:focus {
    outline: none !important;
  }

  // テーブルのカラムヘッダをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-columnHeader:focus-within,
  & .MuiDataGrid-columnHeader:focus {
    outline: none !important;
  }

  // テーブルヘッダーの背景色を設定
  .MuiDataGrid-columnHeaders {
    line-height: 1.5em !important;
  }

  // テーブルタイトルを太字に
  .MuiDataGrid-columnHeaderTitle {
    font-weight: 700;
  }
`;

// 最新所有者注記のTooltip全文
const LATEST_OWNER_ANNOTATION_TEXT =
  "最新の登記では所有者が異なる可能性があります";

/**
 * 最新所有者注記バッジ
 *
 * is_latest_owner=false（前所有者と判明）の行に表示する警告バッジ。
 * ホバーで注記全文をTooltip表示する。
 */
const LatestOwnerAnnotationBadge = (): JSX.Element => {
  return (
    <Tooltip title={LATEST_OWNER_ANNOTATION_TEXT}>
      <Box
        component="span"
        sx={{
          display: "inline-block",
          px: 1,
          py: 0.25,
          fontSize: "0.75rem",
          borderRadius: "999px",
          border: "2px solid",
          borderColor: "warning.main",
          backgroundColor: "transparent",
          color: "warning.main",
          fontWeight: 600,
          textAlign: "center",
        }}
      >
        所有者変更の可能性
      </Box>
    </Tooltip>
  );
};

const makeGridColDef = (
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setDownloadCondition: React.Dispatch<
    React.SetStateAction<StoredPicturesDownloadCondition>
  >,
  apiClient: AxiosInstance,
  navigate: NavigateFunction,
  isReadOnly: boolean
): GridColDef[] => {
  // ダウンロードボタンクリック時に実行。
  function handleClick(row: DataGridRow): void {
    if (isReadOnly) {
      return;
    }
    if (
      row.lastUserAcquisitionDate === null ||
      row.lastUserAcquisitionDate < row.receivedDate
    ) {
      // lastUserAcquisitionDateがnullまたはreceivedDateより前の日付ならば、モーダル表示。
      setIsOpen(true);
      setDownloadCondition({
        billableData: [
          {
            id: row.id,
            price: row.chargeAmount,
            receivedId: row.receivedId,
          },
        ],
        noBillableData: [],
      });
    } else {
      // モーダルを表示せず無料でダウンロード。
      // HACK: この分岐は不要かもしれない、不要の場合はBE側のnoBillableDataの処理と一緒に削除する
      (async () => {
        await downloadStoredPicturesAPI(
          apiClient,
          {
            billableData: [],
            noBillableData: [
              {
                id: row.id,
              },
            ],
          },
          navigate
        );
      })();
    }
  }

  return [
    {
      field: "transitionButton",
      headerName: "詳細画面",
      width: 120,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }: GridRenderCellParams<boolean, DataGridRow>) => {
        if (row.lastUserAcquisitionDate != null) {
          return (
            <LinkButton
              href={`/acquirebookstatus/${row.pictureRequestId}/`}
              variant={ButtonVariantOption.Text}
              wrapperSize={SizingWrapperStyle.SMALL}
            >
              詳細を開く
            </LinkButton>
          );
        } else {
          return (
            <Button variant="text" size="small" disabled>
              詳細を開く
            </Button>
          );
        }
      },
    },
    {
      // NOTE: スクロールせずに視認できるよう、詳細画面と種別の間に配置する
      field: "isLatestOwner",
      headerName: "注記",
      width: 180,
      sortable: false,
      // フィルタは生値（true/false/null）ではなく注記文言に対して行えるよう文字列化する
      valueGetter: ({
        row,
      }: GridValueGetterParams<boolean | null, DataGridRow>) =>
        row.isLatestOwner === false ? "所有者変更の可能性" : "",
      align: "center",
      headerAlign: "center",
      renderCell: ({
        row,
      }: GridRenderCellParams<boolean | null, DataGridRow>) => {
        // false（前所有者と判明）の行のみバッジを表示する（true/null は表示なし）
        if (row.isLatestOwner === false) {
          return <LatestOwnerAnnotationBadge />;
        }
        return <></>;
      },
    },
    {
      field: "realEstateType",
      headerName: "種別",
      width: 150,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }: GridRenderCellParams<string>) => {
        if (value === RealEstateTypeValues.BUILDING)
          return RealEstateTypeLabels.BUILDING;
        else if (value === RealEstateTypeValues.CONDOMINIUM)
          return RealEstateTypeLabels.CONDOMINIUM;
        else return RealEstateTypeLabels.LAND;
      },
    },
    {
      field: "pictureType",
      headerName: "事項種別",
      width: 150,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }: GridRenderCellParams<string>) => {
        if (value === PictureTypeValues.SHOYUSHAJIKO)
          return PictureTypeLabels.SHOYUSHAJIKO;
        else if (value === PictureTypeValues.ZENBUJIKO)
          return PictureTypeLabels.ZENBUJIKO;
        else return null;
      },
    },
    {
      field: "prefectureName",
      headerName: "都道府県",
      width: 150,
      sortable: true,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "location",
      headerName: "所在",
      width: 300,
      sortable: true,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "chibanKaoku",
      headerName: "地番または家屋番号",
      width: 300,
      sortable: true,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "receivedDate",
      headerName: "リユース登記",
      width: 180,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }: GridRenderCellParams<string, DataGridRow>) => {
        if (
          row.receivedDate == null ||
          row.receivedDate === row.lastUserAcquisitionDate
        ) {
          return "";
        } else {
          return format(new Date(row.receivedDate), "yyyy/MM/dd");
        }
      },
    },
    {
      field: "isDownloadable",
      headerName: "ダウンロード",
      width: 120,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }: GridRenderCellParams<boolean, DataGridRow>) => {
        const isDisplay = (row: DataGridRow): boolean => {
          if (
            row.lastUserAcquisitionDate === null ||
            row.lastUserAcquisitionDate < row.receivedDate
          ) {
            return true;
          }
          return false;
        };
        return isDisplay(row) ? (
          <ReadOnlyTooltip isReadOnly={isReadOnly}>
            <IconButton
              aria-label="download-icon"
              color="primary"
              disabled={isReadOnly}
              onClick={() => {
                handleClick(row);
              }}
            >
              <CloudDownloadIcon />
            </IconButton>
          </ReadOnlyTooltip>
        ) : (
          <></>
        );
      },
    },
    {
      field: "lastUserAcquisitionDate",
      headerName: "取得済登記",
      width: 180,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }: GridRenderCellParams<string>) => {
        return value != null ? format(new Date(value), "yyyy/MM/dd") : "";
      },
    },
  ];
};

export const StoredPicturesFeedGrid: React.FC<StoredPicturesFeedGridProps> = ({
  rows,
  setDownloadCondition,
  setIsOpen,
  apiClient,
  setSelectedRowIds,
  navigate,
  maxDownloadCount,
  loading,
  isReadOnly,
}) => {
  const colDef = makeGridColDef(
    setIsOpen,
    setDownloadCondition,
    apiClient,
    navigate,
    isReadOnly
  );
  const [selectionModel, setSelectionModel] =
    React.useState<GridSelectionModel>([]);

  return (
    <StyledDataGrid
      stickyHeader={true}
      autoHeight
      checkboxSelection
      columns={colDef}
      disableColumnMenu={false}
      disableSelectionOnClick={true}
      rows={rows}
      components={{
        ColumnMenu: CustomColumnMenu,
      }}
      selectionModel={selectionModel}
      loading={loading}
      onSelectionModelChange={(newSelectionModel: GridSelectionModel) => {
        if (newSelectionModel.length <= maxDownloadCount) {
          setSelectedRowIds(newSelectionModel);
          setSelectionModel(newSelectionModel);
        }
      }}
    />
  );
};
